import { useState, useEffect, useCallback } from "react";
import {
  Button, Space, Modal, Form, Select, DatePicker, InputNumber, Input, Row, Col,
  Divider, Statistic, message,
} from "antd";
import { PlusOutlined, ReloadOutlined, FilePdfOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import KPICard, { KPIGrid } from "../../components/common/KPICard";
import ExportButton, { downloadBlob } from "../../components/common/ExportButton";
import ConfirmDeleteModal from "../../components/common/ConfirmDeleteModal";
import usePermission from "../../hooks/usePermission";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import { INVOICE_SCHEDULE_FIELDS, VAT_PERCENT, formatOmr } from "./constants";
import {
  listInvoices, getInvoiceKpis, createInvoice, updateInvoice, deleteInvoice,
  downloadInvoicePdf, exportInvoices,
} from "../../services/accounts.service";

const { RangePicker } = DatePicker;
const { TextArea } = Input;

/**
 * Invoice tab.
 *
 * The fee schedule is a fixed set of named lines (from the supplied
 * invoice.pdf), not free-form rows. Subtotal, VAT and final amount are derived
 * by the backend; the preview here mirrors that arithmetic so the operator sees
 * the total before saving.
 */
export default function InvoiceTab() {
  const { canEdit, canDelete } = usePermission("accounts");
  // Only 'company' accounts are valid remittance details on a printed invoice.
  const { options: bankOptions } = useSettingsDropdown("company-bank-accounts", {
    filter: (item) => item.accountTypes?.includes("company"),
  });
  const { options: projectOptions } = useSettingsDropdown("projects");

  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [segment, setSegment] = useState();
  const [dateRange, setDateRange] = useState(null);

  const [modal, setModal] = useState({ open: false, record: null });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState({ subtotal: 0, vatAmount: 0, finalAmount: 0 });
  const [form] = Form.useForm();

  const params = useCallback(
    (extra = {}) => ({
      search: search || undefined,
      segment: segment || undefined,
      fromDate: dateRange?.[0]?.format("YYYY-MM-DD"),
      toDate: dateRange?.[1]?.format("YYYY-MM-DD"),
      ...extra,
    }),
    [search, segment, dateRange]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, kpiResponse] = await Promise.all([
        listInvoices(params({ page, per_page: pageSize })),
        getInvoiceKpis(params()),
      ]);
      setRows(list?.data?.records || []);
      setTotalCount(list?.data?.pagination?.total_count || 0);
      setKpis(kpiResponse?.data || {});
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not load invoices");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [params, page, pageSize]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    setPage(1);
  }, [search, segment, dateRange]);

  /** Mirror the backend derivation so the operator sees the total live. */
  const recomputePreview = useCallback(() => {
    const values = form.getFieldsValue();
    const subtotal = INVOICE_SCHEDULE_FIELDS.reduce(
      (acc, f) => acc + (f.sign ?? 1) * Number(values[f.name] || 0),
      0
    );
    const vatPercent = Number(values.vatPercent ?? VAT_PERCENT);
    const vatAmount = Number(((subtotal * vatPercent) / 100).toFixed(3));
    setPreview({
      subtotal: Number(subtotal.toFixed(3)),
      vatAmount,
      finalAmount: Number((subtotal + vatAmount).toFixed(3)),
    });
  }, [form]);

  const openModal = (record = null) => {
    setModal({ open: true, record });
    if (record) {
      form.setFieldsValue({
        invoiceTo: record.invoiceTo,
        invoiceNo: record.invoiceNo,
        dateOfIssue: record.dateOfIssue ? dayjs(record.dateOfIssue) : null,
        dueDate: record.dueDate ? dayjs(record.dueDate) : null,
        dateOfSupplyFrom: record.dateOfSupplyFrom ? dayjs(record.dateOfSupplyFrom) : null,
        dateOfSupplyTo: record.dateOfSupplyTo ? dayjs(record.dateOfSupplyTo) : null,
        bankAccountTo: record.bankAccountTo?._id,
        customerVatin: record.customerVatin,
        customerAddress: record.customerAddress,
        segment: record.segment?._id,
        vatPercent: record.vatPercent,
        description: record.description,
        ...Object.fromEntries(
          INVOICE_SCHEDULE_FIELDS.map((f) => [f.name, record.schedule?.[f.name] ?? 0])
        ),
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        dateOfIssue: dayjs(),
        vatPercent: VAT_PERCENT,
        ...Object.fromEntries(INVOICE_SCHEDULE_FIELDS.map((f) => [f.name, 0])),
      });
    }
    // Defer so the form has the new values before the preview reads them.
    setTimeout(recomputePreview, 0);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const schedule = Object.fromEntries(
        INVOICE_SCHEDULE_FIELDS.map((f) => [f.name, Number(values[f.name] || 0)])
      );
      const payload = {
        invoiceTo: values.invoiceTo,
        customerVatin: values.customerVatin,
        customerAddress: values.customerAddress,
        invoiceNo: values.invoiceNo,
        dateOfIssue: values.dateOfIssue.format("YYYY-MM-DD"),
        dueDate: values.dueDate?.format("YYYY-MM-DD"),
        dateOfSupplyFrom: values.dateOfSupplyFrom?.format("YYYY-MM-DD"),
        dateOfSupplyTo: values.dateOfSupplyTo?.format("YYYY-MM-DD"),
        bankAccountTo: values.bankAccountTo,
        segment: values.segment,
        vatPercent: values.vatPercent,
        description: values.description,
        schedule,
      };
      if (modal.record) {
        await updateInvoice(modal.record._id, payload);
        message.success("Invoice updated and PDF regenerated");
      } else {
        await createInvoice(payload);
        message.success("Invoice created");
      }
      setModal({ open: false, record: null });
      fetchAll();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not save invoice");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (record) => {
    try {
      const blob = await downloadInvoicePdf(record._id);
      downloadBlob(blob, `${record.invoiceId}.pdf`);
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not download the invoice");
    }
  };

  const handleDelete = (record) => {
    ConfirmDeleteModal({
      title: "Delete Invoice",
      content: `Delete ${record.invoiceId}?`,
      onConfirm: async () => {
        try {
          await deleteInvoice(record._id);
          message.success("Invoice deleted");
          fetchAll();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Could not delete invoice");
        }
      },
    });
  };

  const columns = [
    { title: "Invoice ID", dataIndex: "invoiceId" },
    { title: "Invoice No", dataIndex: "invoiceNo" },
    { title: "Invoice To", dataIndex: "invoiceTo", ellipsis: true },
    { title: "Date of Issue", dataIndex: "dateOfIssueLabel" },
    { title: "Due Date", dataIndex: "dueDateLabel" },
    { title: "Segment", dataIndex: "segmentName" },
    { title: "Subtotal", dataIndex: "subtotal", align: "right", render: formatOmr },
    { title: "VAT", dataIndex: "vatAmount", align: "right", render: formatOmr },
    {
      title: "Final Amount (OMR)",
      dataIndex: "finalAmount",
      align: "right",
      render: (value) => <b>{formatOmr(value)}</b>,
    },
    {
      title: "PDF",
      key: "pdf",
      width: 110,
      render: (_, record) => (
        <Button size="small" icon={<FilePdfOutlined />} onClick={() => handleDownload(record)}>
          Download
        </Button>
      ),
    },
  ];

  return (
    <div>
      <KPIGrid minWidth={170}>
        <KPICard label="Invoices" value={kpis.all} />
        <KPICard label="Subtotal" value={kpis.subtotal} unit="OMR" />
        <KPICard label="VAT" value={kpis.vatAmount} unit="OMR" />
        <KPICard label="Total Invoiced" value={kpis.finalAmount} unit="OMR" tone="dark" />
      </KPIGrid>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search invoice ID, number or client"
          allowClear
          style={{ width: 280 }}
          onSearch={setSearch}
        />
        <Select
          placeholder="Segment"
          allowClear
          style={{ width: 170 }}
          options={projectOptions}
          value={segment}
          onChange={setSegment}
        />
        <RangePicker format="DD/MM/YYYY" onChange={setDateRange} />
        <Button icon={<ReloadOutlined />} onClick={fetchAll}>
          Refresh
        </Button>
        <ExportButton moduleName="Invoices" onExport={() => exportInvoices(params())} />
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
            Create Invoice
          </Button>
        )}
      </Space>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ current: page, pageSize, total: totalCount }}
        onPageChange={setPage}
        actions={{
          onEdit: canEdit ? openModal : undefined,
          onDelete: canDelete ? handleDelete : undefined,
          canEdit,
          canDelete,
        }}
      />

      <Modal
        open={modal.open}
        title={modal.record ? "Edit Invoice" : "Create Invoice"}
        onCancel={() => setModal({ open: false, record: null })}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Save"
        width={820}
      >
        <Form form={form} layout="vertical" onValuesChange={recomputePreview}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="invoiceTo" label="Invoice To" rules={[{ required: true }]}>
                <Input maxLength={300} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="customerVatin"
                label="VATIN"
                rules={[
                  {
                    pattern: /^\d*$/,
                    message: "VATIN must contain digits only",
                  },
                ]}
              >
                <Input maxLength={30} inputMode="numeric" placeholder="e.g. 1313412341" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="customerAddress" label="Address">
                <Input maxLength={100} showCount placeholder="Customer address as it should print on the invoice" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="invoiceNo" label="Invoice No">
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dateOfIssue" label="Date of Issue" rules={[{ required: true }]}>
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dueDate" label="Due Date">
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dateOfSupplyFrom" label="Date of Supply From">
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dateOfSupplyTo" label="Date of Supply To">
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="bankAccountTo" label="Remit To (Bank Account)">
                <Select options={bankOptions} allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="segment" label="Segment">
                <Select options={projectOptions} allowClear />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>
            Fee Schedule (OMR)
          </Divider>
          <Row gutter={16}>
            {INVOICE_SCHEDULE_FIELDS.map((field) => (
              <Col span={8} key={field.name}>
                <Form.Item name={field.name} label={field.label}>
                  <InputNumber style={{ width: "100%" }} min={0} precision={3} />
                </Form.Item>
              </Col>
            ))}
          </Row>

          <Row gutter={16} align="middle">
            <Col span={6}>
              <Form.Item name="vatPercent" label="VAT %">
                <InputNumber style={{ width: "100%" }} min={0} max={100} precision={2} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Statistic title="Subtotal" value={formatOmr(preview.subtotal)} />
            </Col>
            <Col span={6}>
              <Statistic title="VAT" value={formatOmr(preview.vatAmount)} />
            </Col>
            <Col span={6}>
              <Statistic
                title="Final Amount"
                value={formatOmr(preview.finalAmount)}
                valueStyle={{ fontWeight: 700 }}
              />
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} maxLength={2000} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
